import { useCallback, useEffect, useState } from "react";
import { api } from "../api";

// RIGHT AND WRONG — the standing ledger of what is working and what is not,
// each item traced to the bets behind it, explained forward (the world to the
// result) and in reverse (the result back to the cause). Written nightly with
// THE BOOKS; the board reads the same rows every morning. Marking a lesson
// kept / changed / retired is the owner's call and keeps the ledger current.

type Receipt = { exit_order_id: string; symbol?: string; day?: string; pnl?: number; book?: string };
type Item = { id: string; day: string; kind: "right" | "wrong"; title: string; text: string; forward: string; reverse: string;
  keep_or_change: string; evidence: Receipt[]; metrics: Record<string, unknown>; status: string };
type Ledger = { day: string | null; right: Item[]; wrong: Item[] };

export default function RightAndWrong() {
  const [led, setLed] = useState<Ledger | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(() => api.get<Ledger>("/api/performance/lessons").then(setLed).catch(() => setLed(null)), []);
  useEffect(() => { void load(); }, [load]);
  const write = async () => { setBusy(true); try { setLed(await api.post<Ledger>("/api/performance/lessons/write")); } finally { setBusy(false); } };
  const mark = async (id: string, status: string) => { await api.post(`/api/performance/lessons/${id}/status`, { status }); await load(); };
  if (!led) return null;
  const side = (kind: "right" | "wrong") => (
    <div className="space-y-2 min-w-0">
      <div className={`text-[10px] uppercase tracking-widest font-bold ${kind === "right" ? "text-up" : "text-down"}`}>
        {kind === "right" ? "What we are doing right" : "What we are getting wrong"}
      </div>
      {led[kind].length === 0 && <p className="text-xs text-slate-500">nothing qualifies yet — a family needs three closed bets</p>}
      {led[kind].map(it => (
        <div key={it.id} className={`rounded-lg border border-edge border-l-4 ${kind === "right" ? "border-l-up" : "border-l-down"} p-3 ${it.status === "retired" ? "opacity-50" : ""}`}>
          <button className="text-left w-full" onClick={() => setOpen(open === it.id ? null : it.id)}>
            <div className="text-sm font-semibold text-slate-100">{it.text}</div>
            <div className="text-xs text-slate-500 mt-1">{it.keep_or_change}{it.status !== "open" ? ` · ${it.status}` : ""}</div>
          </button>
          {open === it.id && (
            <div className="mt-2 space-y-1 text-sm text-slate-300">
              <p><span className="text-slate-500">Forward · </span>{it.forward}</p>
              <p><span className="text-slate-500">Reverse · </span>{it.reverse}</p>
              {it.evidence.length > 0 && (
                <p className="text-xs text-slate-400">Receipts: {it.evidence.map(e => `${e.symbol ?? ""} ${e.day ?? ""} ${e.pnl != null ? (e.pnl >= 0 ? "+" : "") + e.pnl.toFixed(2) : ""}`).join(" · ")}</p>
              )}
              <div className="flex gap-1 pt-1">
                {["kept", "changed", "retired"].map(st => <button key={st} className="badge bg-ink text-slate-400 !font-normal hover:text-slate-200" onClick={() => void mark(it.id, st)}>{st}</button>)}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="label !mb-0">Right and wrong · traced to the bets{led.day ? ` · ${led.day}` : ""}</h2>
        <button className="btn" onClick={write} disabled={busy}>{busy ? "Writing…" : "Rewrite now"}</button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{side("right")}{side("wrong")}</div>
    </div>
  );
}
